'use client'

/**
 * Sitewide loading indicator: 3 blue arc strips rotating in a circle at different speeds.
 * Colors: #77a4d7 (light), #4685c5 (medium), #27598e (dark).
 */
const COLORS = {
  light: '#77a4d7',
  medium: '#4685c5',
  dark: '#27598e',
} as const

export interface LoadingImageProps {
  /** Size in pixels. Default 40. */
  size?: number
  /** Optional additional class names. */
  className?: string
  /** Accessibility label. */
  ariaLabel?: string
}

export function LoadingImage({ size = 40, className = '', ariaLabel = 'Loading' }: LoadingImageProps) {
  const cx = 50
  const cy = 50
  const stroke = 5
  const r1 = 42
  const r2 = 31
  const r3 = 20
  const arcFrac = 0.75
  const dash1 = `${arcFrac * 2 * Math.PI * r1} ${(1 - arcFrac) * 2 * Math.PI * r1}`
  const dash2 = `${arcFrac * 2 * Math.PI * r2} ${(1 - arcFrac) * 2 * Math.PI * r2}`
  const dash3 = `${arcFrac * 2 * Math.PI * r3} ${(1 - arcFrac) * 2 * Math.PI * r3}`

  return (
    <span
      className={`inline-flex items-center justify-center ${className}`}
      role="status"
      aria-label={ariaLabel}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        className="overflow-visible"
        style={{ transform: 'rotate(-90deg)' }}
      >
        <defs>
          <style>{`
            @keyframes loading-strip-1 { to { transform: rotate(360deg); } }
            @keyframes loading-strip-2 { to { transform: rotate(360deg); } }
            @keyframes loading-strip-3 { to { transform: rotate(360deg); } }
            .loading-strip-1 { animation: loading-strip-1 1.15s linear infinite; transform-origin: ${cx}px ${cy}px; }
            .loading-strip-2 { animation: loading-strip-2 1.62s linear infinite; transform-origin: ${cx}px ${cy}px; }
            .loading-strip-3 { animation: loading-strip-3 2.08s linear infinite; transform-origin: ${cx}px ${cy}px; }
          `}</style>
        </defs>
        <circle
          cx={cx}
          cy={cy}
          r={r1}
          fill="none"
          stroke={COLORS.dark}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={dash1}
          className="loading-strip-1"
        />
        <circle
          cx={cx}
          cy={cy}
          r={r2}
          fill="none"
          stroke={COLORS.medium}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={dash2}
          className="loading-strip-2"
        />
        <circle
          cx={cx}
          cy={cy}
          r={r3}
          fill="none"
          stroke={COLORS.light}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={dash3}
          className="loading-strip-3"
        />
      </svg>
    </span>
  )
}
